// Связки сделок. Только чтение.
//
// Раздел отвечает на вопрос о СОСТОЯНИИ ПРИВЯЗОК зеркала: какая доля записей
// смарт-процессов привязана к сделке, какая доля сделок покрыта связками, где ссылка
// ведёт в никуда — и что именно привязано к одной конкретной сделке. Это не «где встала
// работа» (это «Путь сделки») и не «сколько денег между учётами» (это «Сквозные
// отчёты»): здесь судят о том, каким разделам зеркала можно верить «по сделке».
//
// Правило показа одно и жёсткое: «не привязано», «оборвано» и «привязано» — ТРИ разных
// ответа, и на экране они не склеиваются нигде. Расчётов здесь нет: всё считает ядро
// (xlinks_api.py), экран только показывает.

import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { Обрыв, Привязка } from '../api/xlinks'
import { useXLDeal, useXLObryvy, useXLPokrytie, useXLSprav } from '../api/xlinks'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, fmtText, pluralN } from '../lib/format'

// ────────────────────────── общие куски разметки ──────────────────────────

const Пусто = ({ колонок, текст }: { колонок: number; текст: string }) => (
  <tr>
    <td colSpan={колонок} className="empty" data-label="">
      <div className="empty-title">{текст}</div>
    </td>
  </tr>
)

/** Оговорки ядра: приходят вместе с данными, а не пишутся на экране. */
const Оговорки = ({ заметки }: { заметки?: Record<string, string> }) => {
  if (!заметки) return null
  return (
    <ul className="hint" style={{ marginTop: 10, paddingLeft: 18 }}>
      {Object.entries(заметки).map(([к, т]) => <li key={к}>{т}</li>)}
    </ul>
  )
}

/** Чем запись нашлась. У «не parentId2» подпись обязана быть: молчание про способ
 *  поиска превращает «нашлась полем» в «нашлась как все». */
const какНайдена = (р: Привязка): string => {
  if (р.by_parent === true) return 'parentId2'
  if (р.by_parent === false) return 'своё поле-привязки'
  return 'мостик портала 73'
}

// ────────────────────────────── экран ──────────────────────────────

export default function DealLinks() {
  const справ = useXLSprav()
  const обрывы = useXLObryvy()
  const покрытие = useXLPokrytie()

  // Номер сделки применяется кнопкой/Enter, а не на каждый символ: запрос по одной
  // цифре «1» — это честный ответ про чужую сделку, который никто не спрашивал.
  const [ввод, setВвод] = useState('')
  const [сделкаId, setСделкаId] = useState<number | null>(null)
  const сделка = useXLDeal(сделкаId)

  const применить = () => {
    const n = parseInt(ввод.trim(), 10)
    setСделкаId(Number.isFinite(n) && n > 0 ? n : null)
  }

  return (
    <>
      <div className="page-head">
        <h1>Связки сделок</h1>
      </div>

      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Здесь показано, насколько данные зеркала связаны между собой.</b> Сделка,
        записи смарт-процессов и работы портала 73 живут в разных источниках; этот раздел
        считает привязки между ними и показывает, где ссылка ведёт в никуда. «Не
        привязано», «оборвано» и «привязано» — три разных ответа, и они не складываются.
        Оборванная ссылка показывается, а не чинится: править её — в источнике и его
        владельцу.
      </div>

      {/* ── 1. Виды связок ── */}
      <QueryState isLoading={справ.isLoading} error={справ.error}>
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Виды связок</div>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Вид</th>
                  <th>Как связано</th>
                  <th className="num">Записей</th>
                  <th className="num">Привязано</th>
                  <th className="num">Не привязано</th>
                  <th className="num">Оборвано</th>
                </tr>
              </thead>
              <tbody>
                {(справ.data?.rows ?? []).map((в) => (
                  <tr key={в.kind}>
                    <td data-label="Вид">{в.title}</td>
                    <td data-label="Как связано" className="hint">{в.link}</td>
                    <td className="num" data-label="Записей">{в.rows}</td>
                    <td className="num" data-label="Привязано">{в.linked}</td>
                    <td className="num" data-label="Не привязано">{в.unlinked}</td>
                    <td className="num" data-label="Оборвано"
                        style={{ fontWeight: в.broken > 0 ? 600 : 400 }}>
                      {в.broken}
                    </td>
                  </tr>
                ))}
                {справ.data && справ.data.rows.length === 0 && (
                  <Пусто колонок={6} текст="Ядро не назвало ни одного вида связок" />
                )}
              </tbody>
            </table>
          </div>
          <Оговорки заметки={справ.data?.data_notes} />
        </div>
      </QueryState>

      {/* ── 2. Покрытие сделок ── */}
      <QueryState isLoading={покрытие.isLoading} error={покрытие.error}>
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Покрытие сделок связками</div>
          {покрытие.data && (
            <div className="hint" style={{ marginBottom: 6 }}>
              {`Сделки, заведённые с ${fmtDate(покрытие.data.from)}: `}
              {pluralN(покрытие.data.total, 'сделка', 'сделки', 'сделок')}
              {'. Доля — у скольких из них есть хотя бы одна запись вида.'}
            </div>
          )}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Вид</th>
                  <th className="num">Сделок со связкой</th>
                  <th className="num">Доля</th>
                </tr>
              </thead>
              <tbody>
                {(покрытие.data?.rows ?? []).map((р) => (
                  <tr key={р.kind}>
                    <td data-label="Вид">{р.title}</td>
                    <td className="num" data-label="Сделок со связкой">{р.deals_linked}</td>
                    <td className="num" data-label="Доля">
                      {р.share_pct === null
                        ? <span className="muted">окно пусто</span>
                        : `${р.share_pct} %`}
                    </td>
                  </tr>
                ))}
                {покрытие.data && покрытие.data.rows.length === 0 && (
                  <Пусто колонок={3} текст="Ядро не отдало покрытие ни по одному виду" />
                )}
              </tbody>
            </table>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>
            Низкая доля сама по себе не нарушение: у сервисной сделки не бывает отгрузки,
            у поставки — АВР. Доля говорит о том, ведут ли привязку вообще.
          </div>
        </div>
      </QueryState>

      {/* ── 3. Связки в никуда ── */}
      <QueryState isLoading={обрывы.isLoading} error={обрывы.error}>
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Связки в никуда</div>
          {обрывы.data && обрывы.data.rows.length === 0 && (
            <div className="empty">
              <div className="empty-title">Оборванных ссылок сейчас нет</div>
              <div className="hint">
                Ни одна заполненная привязка не указывает на сделку, которой нет в зеркале.
              </div>
            </div>
          )}
          {(обрывы.data?.rows ?? []).map((о) => <БлокОбрыва key={о.kind} о={о} />)}
          <Оговорки заметки={обрывы.data?.data_notes} />
        </div>
      </QueryState>

      {/* ── 4. Привязки одной сделки ── */}
      <div className="card">
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Привязки одной сделки</div>
        <FilterBar id="xlinks-deal" style={{ marginBottom: 10 }}>
          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="xl-deal-id">Сделка Bitrix №</label>
            <input id="xl-deal-id" inputMode="numeric" value={ввод}
                   onChange={(e) => setВвод(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') применить() }}
                   placeholder="например, 107623" />
          </div>
          <div className="f" style={{ alignSelf: 'end' }}>
            <button onClick={применить}>Показать</button>
          </div>
        </FilterBar>

        {сделкаId === null && (
          <div className="hint">
            Введите номер сделки — раздел покажет все её привязки: записи смарт-процессов
            (включая найденные не parentId2, а своим полем), заявки и работы портала 73.
          </div>
        )}

        {сделкаId !== null && (
          <QueryState isLoading={сделка.isLoading} error={сделка.error}>
            {сделка.data?.not_found ? (
              <div className="notice">Ядро отвечает: {сделка.data.not_found}.</div>
            ) : (
              <>
                {сделка.data?.deal && (
                  <div style={{ marginBottom: 8 }}>
                    <Link to={`/deals/${сделка.data.deal.bitrix_id}`}>
                      {fmtText(сделка.data.deal.title) || `#${сделка.data.deal.bitrix_id}`}
                    </Link>{' '}
                    <span className="hint">
                      {fmtMoney(сделка.data.deal.opportunity,
                                сделка.data.deal.currency ?? 'RUB')}
                      {' · привязок: '}{сделка.data.total}
                    </span>
                  </div>
                )}
                <div className="table-wrap cards">
                  <table>
                    <thead>
                      <tr>
                        <th>Вид</th>
                        <th>Запись</th>
                        <th>Состояние</th>
                        <th className="num">Сумма</th>
                        <th className="nowrap">Создана</th>
                        <th>Чем нашлась</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(сделка.data?.rows ?? []).map((р) => (
                        <tr key={`${р.kind}-${р.id}`}>
                          <td data-label="Вид">{р.title}</td>
                          <td data-label="Запись">
                            {fmtText(р.name) || `#${р.id}`}
                          </td>
                          <td data-label="Состояние" className="hint">{fmtText(р.state)}</td>
                          <td className="num" data-label="Сумма">
                            {р.sum === null ? '' : fmtMoney(р.sum, р.currency ?? 'RUB')}
                          </td>
                          <td className="nowrap" data-label="Создана">{fmtDate(р.created)}</td>
                          <td data-label="Чем нашлась" className="hint">{какНайдена(р)}</td>
                        </tr>
                      ))}
                      {сделка.data && сделка.data.rows.length === 0 && (
                        <Пусто колонок={6}
                               текст="К этой сделке не привязано ни одной записи" />
                      )}
                    </tbody>
                  </table>
                </div>
                <Оговорки заметки={сделка.data?.data_notes} />
              </>
            )}
          </QueryState>
        )}
      </div>
    </>
  )
}

// ────────────────────────── блок одного вида обрывов ──────────────────────────

function БлокОбрыва({ о }: { о: Обрыв }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ marginBottom: 4 }}>
        <b>{о.title}</b>{' — '}
        {pluralN(о.count, 'оборванная ссылка', 'оборванные ссылки', 'оборванных ссылок')}
      </div>
      <div className="hint" style={{ marginBottom: 6 }}>{о.why}</div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th className="num">Запись</th>
              <th className="num">Ссылка ведёт на</th>
              <th>Название</th>
              <th className="nowrap">Создана</th>
            </tr>
          </thead>
          <tbody>
            {о.examples.map((п) => (
              <tr key={п.id}>
                <td className="num" data-label="Запись">#{п.id}</td>
                <td className="num" data-label="Ссылка ведёт на">
                  {/* Не <Link>: сделки за этим номером нет, вести туда некуда. */}
                  {п.ref === null ? '' : `сделку №${п.ref}`}
                  {п.bridge_lost && (
                    <div className="hint">заявки портала нет в зеркале</div>
                  )}
                </td>
                <td data-label="Название">{fmtText(п.title)}</td>
                <td className="nowrap" data-label="Создана">{fmtDate(п.created)}</td>
              </tr>
            ))}
            {о.examples.length === 0 && (
              <Пусто колонок={4} текст="Примеры не приехали — счётчик выше посчитан ядром" />
            )}
          </tbody>
        </table>
      </div>
      {о.count > о.examples.length && о.examples.length > 0 && (
        <div className="hint" style={{ marginTop: 4 }}>
          На экране {pluralN(о.examples.length, 'пример', 'примера', 'примеров')} из {о.count}.
        </div>
      )}
    </div>
  )
}
